/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, faEyeSlash, faUser, faLock, faEnvelope, faArrowRight,
  faCheck, faTimes, faBriefcase, faGraduationCap, faUserTie,
  faBuilding, faUsers, faShieldAlt, faPhone, faUniversity, faHome, faHandsHelping
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import logoImage from '../assets/FINAL LOGO AK.png';
import PhoneInput from '../components/PhoneInput';
import AuthMobileAurora from '../components/AuthMobileAurora';
import '../styles/Signup.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'jobSeeker', // Set default role
    agreeToTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const [platformStats, setPlatformStats] = useState({
    activeJobs: null,
    companies: null,
    jobSeekers: null
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  const { signup } = useAuth();
  const navigate = useNavigate();

  const roleOptions = [
    { value: 'jobSeeker', label: 'Job Seeker', icon: faUser, description: 'Looking for job opportunities' },
    { value: 'intern', label: 'Internship / Attachment', icon: faGraduationCap, description: 'Seeking internship positions' },
    { value: 'recruiter', label: 'Recruiter', icon: faUserTie, description: 'Hiring talent for companies' },
    { value: 'employer', label: 'Employer', icon: faBuilding, description: 'Post jobs and hire talent' },
    { value: 'university', label: 'University', icon: faUniversity, description: 'Manage student placements and internships' },
    { value: 'institute', label: 'Institute', icon: faUniversity, description: 'Manage trainee placements' },
    { value: 'association', label: 'Associations / Communities', icon: faUsers, description: 'Manage member organizations' },
    { value: 'ngo', label: 'NGO', icon: faHandsHelping, description: 'Hire talent and volunteers' }
  ];

  // Format statistics numbers with K/M suffixes
  const formatStatValue = (value, addPlus = true) => {
    if (value === null || value === undefined) {
      return statsLoading ? '...' : '0';
    }

    const numericValue = Number(value) || 0;

    if (numericValue >= 1_000_000) {
      const formatted = (numericValue / 1_000_000).toFixed(numericValue >= 10_000_000 ? 0 : 1).replace(/\.0$/, '');
      return `${formatted}M${addPlus && numericValue > 0 ? '+' : ''}`;
    }

    if (numericValue >= 1_000) {
      const formatted = (numericValue / 1_000).toFixed(numericValue >= 10_000 ? 0 : 1).replace(/\.0$/, '');
      return `${formatted}K${addPlus && numericValue > 0 ? '+' : ''}`;
    }

    return `${numericValue.toLocaleString()}${addPlus && numericValue > 0 ? '+' : ''}`;
  };

  // Fetch platform statistics
  useEffect(() => {
    const fetchPlatformStats = async () => {
      try {
        setStatsLoading(true);
        const response = await fetch(buildApiUrl('/api/public/stats-summary'));
        if (!response.ok) {
          throw new Error(`Failed to fetch stats: ${response.status}`);
        }

        const payload = await response.json();
        if (payload?.success && payload.data) {
          const data = payload.data;
          setPlatformStats({
            activeJobs: data.activeJobs ?? 0,
            companies: data.companies ?? 0,
            jobSeekers: data.jobSeekers ?? 0
          });
        } else {
          console.warn('Stats summary did not return success flag.', payload);
        }
      } catch (error) {
        console.error('Error fetching platform stats:', error);
        // Keep default values on error
      } finally {
        setStatsLoading(false);
      }
    };

    fetchPlatformStats();
  }, []);

  // Build benefits array with real statistics
  const benefits = [
    { icon: faBriefcase, title: `${formatStatValue(platformStats.activeJobs)} Active Jobs`, description: 'Find opportunities across all industries' },
    { icon: faBuilding, title: `${formatStatValue(platformStats.companies)} Companies`, description: 'Connect with top employers worldwide' },
    { icon: faUsers, title: `${formatStatValue(platformStats.jobSeekers)} Job Seekers`, description: 'Join our thriving professional community' },
    { icon: faShieldAlt, title: '100% Verified', description: 'All jobs and companies are verified for authenticity' },
  ];

  const checkPasswordStrength = (password) => {
    const feedback = [];
    let score = 0;

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('At least 8 characters');
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One lowercase letter');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One uppercase letter');
    }

    if (/[0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One number');
    }

    if (/[^A-Za-z0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('One special character');
    }

    setPasswordStrength({ score, feedback });
  };

  const handleChange = (e) => {
    // Handle PhoneInput which passes value directly, not an event object
    if (typeof e === 'string') {
      setFormData({
        ...formData,
        phone: e
      });
      if (error) setError('');
      return;
    }
    
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    if (name === 'password') {
      checkPasswordStrength(value);
    }

    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordStrength.score < 3) {
      setError('Password is too weak. Please strengthen your password.');
      setLoading(false);
      return;
    }

    if (!formData.agreeToTerms) {
      setError('Please agree to the terms and conditions');
      setLoading(false);
      return;
    }

    try {
      const signupPayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phone,
        password: formData.password,
        userType: formData.role
      };

      // Send OTP to email for verification
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/auth/signup-otp/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName
        })
      });

      const data = await response.json();

      if (data.verificationDisabled) {
        try {
          await signup(signupPayload);
        } catch (signupError) {
          console.error('Error completing signup without OTP verification:', signupError);
          setError(signupError.message || 'Failed to create your account. Please try again.');
        }
        return;
      }

      if (data.success) {
        // Redirect to email verification page with signup data
        navigate('/verify-email', {
          state: {
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            signupData: signupPayload
          }
        });
      } else {
        // Show error on signup form
        const errorMsg = data.error || 'Failed to send verification code. Please try again.';
        setError(errorMsg);
        
        // If email exists, suggest login
        if (data.errorType === 'EMAIL_EXISTS') {
          setError('This email is already registered. Please login or use a different email.');
        }
      }
    } catch (err) {
      console.error('Error sending OTP:', err);
      if (err.message) {
        setError(err.message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (score) => {
    if (score < 2) return '#e53e3e';
    if (score < 4) return '#dd6b20';
    return '#38a169';
  };

  const getStrengthText = (score) => {
    if (score < 2) return 'Weak';
    if (score < 4) return 'Medium';
    return 'Strong';
  };

  const handleGoogleSignup = async () => {
    try {
      setError('');
      setLoading(true);

      const response = await fetch(buildApiUrl('/api/auth/google/auth'));
      const data = await response.json();

      if (response.ok && data.auth_url) {
        // Redirect user to Google's OAuth consent screen
        window.location.href = data.auth_url;
      } else {
        setError(data.error || 'Unable to start Google sign-up. Please try again.');
      }
    } catch (e) {
      console.error('Google signup error', e);
      setError('Unable to start Google sign-up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const leftVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const rightVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const formVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: 0.2
      }
    }
  };

  return (
    <div className="signup-container">
      <AuthMobileAurora />

      <div className="signup-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="signup-mobile-header">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="back-to-home-btn"
        >
          <FontAwesomeIcon icon={faHome} />
          Back to Home
        </button>
      </div>
      
      <div className="signup-layout">
        <motion.div 
          className="signup-left"
          variants={leftVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="left-content">
            {/* Moved logo and tagline here */}
            <div className="benefits-section">
              <img src={logoImage} alt="AksharJobs Logo" className="main-logo" style={{ marginBottom: '0.25rem' }} />
              <p className="tagline" style={{ marginTop: '0.25rem', marginBottom: '0.25rem' }}>CONNECT | DISCOVER | ELEVATE</p>
              <h2 style={{ marginTop: '0.25rem' }}>Why Choose AksharJobs?</h2>
              <div className="benefits-grid">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    className="benefit-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                  >
                    <div className="benefit-icon">
                      <FontAwesomeIcon icon={benefit.icon} />
                    </div>
                    <div className="benefit-content">
                      <h3>{benefit.title}</h3>
                      <p>{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          className="signup-right"
          variants={rightVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className="signup-card"
            variants={formVariants}
          >
            <div className="signup-header">
              <h2>Create Your Account</h2>
              <p>Join thousands of professionals finding their dream careers</p>
            </div>

          <form onSubmit={handleSubmit} className="signup-form">
            <div className="form-row">
              <motion.div 
                className="form-group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="label-group">
                  <FontAwesomeIcon icon={faUser} className="form-label-icon" />
                  <label htmlFor="firstName">First Name</label>
                </div>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    required
                  />
                </div>
              </motion.div>

              <motion.div 
                className="form-group"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
              >
                <div className="label-group">
                  <FontAwesomeIcon icon={faUser} className="form-label-icon" />
                  <label htmlFor="lastName">Last Name</label>
                </div>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </motion.div>
            </div>

            <motion.div 
              className="form-group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
            >
              <div className="label-group">
                <FontAwesomeIcon icon={faEnvelope} className="form-label-icon" />
                <label htmlFor="email">Email Address</label>
              </div>
              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>
            </motion.div>

            <motion.div 
              className="form-group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.85 }}
            >
              <div className="label-group">
                <FontAwesomeIcon icon={faPhone} className="form-label-icon" />
                <label htmlFor="phone">Phone Number</label>
              </div>
              <div className="input-wrapper">
                <PhoneInput
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="234 567 8900"
                  required
                />
              </div>
            </motion.div>

            <motion.div 
              className="form-group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
            >
              <div className="label-group">
                <FontAwesomeIcon icon={faUser} className="form-label-icon" />
                <label htmlFor="role">I am a:</label>
              </div>
              <div className="input-wrapper">
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="role-dropdown"
                >
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} - {role.description}
                    </option>
                  ))}
                </select>
                <div className="dropdown-icon">
                  <FontAwesomeIcon icon={faUser} />
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="form-group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.0 }}
            >
              <div className="label-group">
                <FontAwesomeIcon icon={faLock} className="form-label-icon" />
                <label htmlFor="password">Password</label>
              </div>
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              
              {formData.password && (
                <motion.div 
                  className="password-strength"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="strength-bar">
                    <div 
                      className="strength-fill"
                      style={{ 
                        width: `${(passwordStrength.score / 5) * 100}%`,
                        backgroundColor: getStrengthColor(passwordStrength.score)
                      }}
                    ></div>
                  </div>
                  <div className="strength-info">
                    <span className="strength-text">
                      Password Strength: <strong style={{ color: getStrengthColor(passwordStrength.score) }}>
                        {getStrengthText(passwordStrength.score)}
                      </strong>
                    </span>
                    {passwordStrength.feedback.length > 0 && (
                      <div className="strength-requirements">
                        {passwordStrength.feedback.map((req, index) => (
                          <span key={index} className="requirement">
                            <FontAwesomeIcon icon={faTimes} className="requirement-icon" />
                            {req}
                          </span>
                        ))}
                      </div>
                    )}
                    {passwordStrength.score >= 3 && (
                      <div className="strength-success">
                        Great! Your password is strong enough.
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>

            <motion.div 
              className="form-group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1 }}
            >
              <div className="label-group">
                <FontAwesomeIcon icon={faLock} className="form-label-icon" />
                <label htmlFor="confirmPassword">Confirm Password</label>
              </div>
              <div className="input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <div className="password-mismatch">
                  <FontAwesomeIcon icon={faTimes} />
                  Passwords do not match
                </div>
              )}
            </motion.div>

            {error && (
              <motion.div 
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </motion.div>
            )}

            <motion.div 
              className="form-options"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <label className="checkbox-wrapper">
                <input 
                  type="checkbox" 
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  required
                />
                <span className="checkmark"></span>
                I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">Terms & Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="terms-link">Privacy Policy</a>
              </label>
            </motion.div>

            <motion.button
              type="submit"
              className="signup-button"
              disabled={loading}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 }}
              whileHover={{ scale: 1.005 }}
              whileTap={{ scale: 0.995 }}
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <>
                  Create Account
                  <FontAwesomeIcon icon={faArrowRight} />
                </>
              )}
            </motion.button>
          </form>

          <div className="signup-oauth-section">
            <div className="oauth-divider">
              <span>or</span>
            </div>
            <motion.button
              type="button"
              className="google-login-button"
              onClick={handleGoogleSignup}
              disabled={loading}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35 }}
              whileHover={{ scale: 1.005 }}
              whileTap={{ scale: 0.995 }}
            >
              <span className="google-login-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  style={{ width: 18, height: 18 }}
                >
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.2C12.27 13.72 17.7 9.5 24 9.5z"
                  />
                  <path
                    fill="#34A853"
                    d="M46.98 24.55c0-1.57-.14-3.08-.39-4.55H24v9.02h12.94c-.56 2.9-2.26 5.36-4.82 7.01l7.79 6.04C44.93 37.67 46.98 31.62 46.98 24.55z"
                  />
                  <path
                    fill="#4285F4"
                    d="M9.54 28.58A14.5 14.5 0 0 1 8.83 24c0-1.59.27-3.12.75-4.55l-7.02-5.47A23.86 23.86 0 0 0 0 24c0 3.82.92 7.42 2.56 10.6l6.98-6.02z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M24 48c6.48 0 11.93-2.13 15.9-5.79l-7.79-6.04C29.9 37.68 27.17 38.5 24 38.5c-6.29 0-11.63-4.21-13.53-9.92l-7.98 6.02C6.51 42.62 14.62 48 24 48z"
                  />
                </svg>
              </span>
              <span className="google-login-text">Sign up with Google</span>
            </motion.button>
          </div>

            <motion.div 
              className="signup-footer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
            >
              <p>
                Already have an account? 
                <Link to="/login" className="login-link"> Sign in</Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
